import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Timer } from "three/addons/misc/Timer.js";
import GUI from "lil-gui";
import { normalMap } from "three/examples/jsm/nodes/Nodes.js";
import { ThreeMFLoader } from "three/examples/jsm/Addons.js";
import { RectAreaLightHelper } from "three/examples/jsm/Addons.js";
import { Sky } from "three/examples/jsm/Addons.js";

/**
 * Base
 */
// Debug
const gui = new GUI();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();
const axisHelper = new THREE.AxesHelper(3);
scene.add(axisHelper);
axisHelper.visible = false;

/**
 * Sky
 */
const sky = new Sky()
sky.material.uniforms['turbidity'].value = 10.16
sky.material.uniforms['rayleigh'].value = 2.524
sky.material.uniforms['mieCoefficient'].value = 0.1
sky.material.uniforms['mieDirectionalG'].value = 0.902
sky.material.uniforms['sunPosition'].value.set(-1, -0.046, -0.868)
sky.scale.set(100, 100, 100)
scene.add(sky)

const skyFolder = gui.addFolder('Sky');
skyFolder.add(sky.material.uniforms['turbidity'], 'value', 0, 20, 0.1).name('Turbidity');
skyFolder.add(sky.material.uniforms['rayleigh'], 'value', 0, 4, 0.1).name('Rayleigh');
skyFolder.add(sky.material.uniforms['mieCoefficient'], 'value', 0, 0.1, 0.001).name('Mie Coefficient');
skyFolder.add(sky.material.uniforms['mieDirectionalG'], 'value', 0, 1, 0.1).name('Mie Directional G');

// Sun position controls
const sunPosition = {
    x: -1,
    y: -0.046,
    z: -0.868
};
skyFolder.add(sunPosition, 'x', -1, 1).onChange(value => {
    sky.material.uniforms['sunPosition'].value.setX(value);
}).name('Sun Position X');
skyFolder.add(sunPosition, 'y', -1, 1).onChange(value => {
    sky.material.uniforms['sunPosition'].value.setY(value);
}).name('Sun Position Y');
skyFolder.add(sunPosition, 'z', -1, 1).onChange(value => {
    sky.material.uniforms['sunPosition'].value.setZ(value);
}).name('Sun Position Z');

/**
 * fog
 */
scene.fog = new THREE.FogExp2('#04343f', 0.1)

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader();

//floor
const floorAlphaTexture = textureLoader.load("./floor/alpha.webp");
const floorColorTexture = textureLoader.load("./floor/textures/coast_sand_rocks_02_diff_1k.webp");
const floorARMTexture = textureLoader.load("./floor/textures/coast_sand_rocks_02_arm_1k.webp");
const floorDisplacementTexture = textureLoader.load("./floor/textures/coast_sand_rocks_02_disp_1k.webp");
const floorNormalTexture = textureLoader.load("./floor/textures/coast_sand_rocks_02_nor_gl_1k.webp");

floorColorTexture.repeat.set(8, 8);
floorARMTexture.repeat.set(8, 8);
floorNormalTexture.repeat.set(8, 8);
floorDisplacementTexture.repeat.set(8, 8);

floorColorTexture.wrapS = THREE.RepeatWrapping;
floorARMTexture.wrapS = THREE.RepeatWrapping;
floorNormalTexture.wrapS = THREE.RepeatWrapping;
floorDisplacementTexture.wrapS = THREE.RepeatWrapping;

floorColorTexture.wrapT = THREE.RepeatWrapping;
floorARMTexture.wrapT = THREE.RepeatWrapping;
floorNormalTexture.wrapT = THREE.RepeatWrapping;
floorDisplacementTexture.wrapT = THREE.RepeatWrapping;

floorColorTexture.colorSpace = THREE.SRGBColorSpace;

//Chest Texture
const chestColorTexture = textureLoader.load("./Chest/ColorMap.webp");
const chestARMTexture = textureLoader.load("./Chest/AmbientOcclusionMap.webp");
const chestDisplacementTexture = textureLoader.load("./Chest/DisplacementMap.webp");
const chestNormalTexture = textureLoader.load("./Chest/NormalMap.webp");

chestColorTexture.colorSpace = THREE.SRGBColorSpace;

//Coin Texture
const coinsColorTexture = textureLoader.load("./Coins/ColorMap.webp");
const coinsARMTexture = textureLoader.load("./Coins/AmbientOcclusionMap.webp");
const coinsDisplacementTexture = textureLoader.load("./Coins/DisplacementMap.webp");
const coinsNormalTexture = textureLoader.load("./Coins/NormalMap.webp");

coinsColorTexture.colorSpace = THREE.SRGBColorSpace;

//shade texture
const shadeColorTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_diff_1k.webp");
const shadeARMTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_arm_1k.webp");
const shadeNormalTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_nor_gl_1k.webp");

shadeColorTexture.colorSpace = THREE.SRGBColorSpace;

shadeColorTexture.repeat.set(4,1);
shadeARMTexture.repeat.set(4,1);
shadeNormalTexture.repeat.set(4,1);

shadeColorTexture.wrapS = THREE.RepeatWrapping;
shadeARMTexture.wrapS = THREE.RepeatWrapping;
shadeNormalTexture.wrapS = THREE.RepeatWrapping;


//shade pillar texture
const pillarColorTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_diff_1k.webp");
const pillarARMTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_arm_1k.webp");
// const pillarDisplacementTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_disp_1k.webp");
const pillarNormalTexture = textureLoader.load("./ShadeTop/wood_planks_dirt_nor_gl_1k.webp");

pillarColorTexture.colorSpace = THREE.SRGBColorSpace;

//graves texture
const graveColorTexture = textureLoader.load("./Pillar/chinese_cedar_bark_diff_1k.webp");
const graveARMTexture = textureLoader.load("./Pillar/chinese_cedar_bark_arm_1k.webp");
const graveDisplacementTexture = textureLoader.load("./Pillar/chinese_cedar_bark_disp_1k.webp");
const graveNormalTexture = textureLoader.load("./Pillar/chinese_cedar_bark_nor_gl_1k.webp");

graveColorTexture.colorSpace = THREE.SRGBColorSpace;

/**
 * Geometries
 */

//chest
const chest = new THREE.Group();

const chestBox = new THREE.Mesh(
  new THREE.BoxGeometry(2, 0.7, 1, 100, 100),
  new THREE.MeshStandardMaterial({
    // wireframe : true,
    map: chestColorTexture,
    normalMap: chestNormalTexture,
    displacementMap: chestDisplacementTexture,
    aoMap: chestARMTexture,
    roughnessMap: chestARMTexture,
    metalnessMap: chestARMTexture,
    displacementScale: 0.021,
    displacementBias: -0.007,
  })
);
chestBox.position.y = 0.35;

gui
  .add(chestBox.material, "displacementScale")
  .min(0)
  .max(1)
  .step(0.001)
  .name("chestDisplacementScale");
gui
  .add(chestBox.material, "displacementBias")
  .min(-1)
  .max(1)
  .step(0.001)
  .name("chestDisplacementBias");

const chestTop = new THREE.Mesh(
  new THREE.CylinderGeometry(0.5, 0.5, 2, 20, 20, false, 0, Math.PI),
  new THREE.MeshStandardMaterial({
    // wireframe:true,
    side: THREE.DoubleSide,
    map: chestColorTexture,
    normalMap: chestNormalTexture,
    displacementMap: chestDisplacementTexture,
    aoMap: chestARMTexture,
    roughnessMap: chestARMTexture,
    metalnessMap: chestARMTexture,
    displacementScale: 0.041,
    displacementBias: -0.01,
  })
);

chestTop.position.y = 1;
chestTop.position.z = -0.1;
chestTop.rotation.z = Math.PI * 0.5;
chestTop.rotation.x = -Math.PI * 0.25;

gui
  .add(chestTop.material, "displacementScale")
  .min(0)
  .max(1)
  .step(0.001)
  .name("chestTOPDisplacementScale");
gui
  .add(chestTop.material, "displacementBias")
  .min(-1)
  .max(1)
  .step(0.001)
  .name("chestTOPDisplacementBias");

//coins
const coins = new THREE.Mesh(
  new THREE.PlaneGeometry(1.9, 0.9, 100, 100),
  new THREE.MeshStandardMaterial({
    // wireframe : true
    map: coinsColorTexture,
    normalMap: coinsNormalTexture,
    displacementMap: coinsDisplacementTexture,
    aoMap: coinsARMTexture,
    roughnessMap: coinsARMTexture,
    metalnessMap: coinsARMTexture,
    displacementScale: 0.061,
    displacementBias: -0.024,
  })
);
coins.position.y = 0.7001;
coins.rotation.x = -Math.PI * 0.5;

gui
  .add(coins.material, "displacementScale")
  .min(0)
  .max(1)
  .step(0.001)
  .name("coinsDisplacementScale");
gui
  .add(coins.material, "displacementBias")
  .min(-1)
  .max(1)
  .step(0.001)
  .name("coinsDisplacementBias");

chest.add(chestBox, chestTop, coins);
scene.add(chest);

//graves
const graves = new THREE.Group();

const graveGeometry = new THREE.BoxGeometry(0.7, 1, 0.2, 100, 100);
const graveMaterial = new THREE.MeshStandardMaterial({
  map: graveColorTexture,
  normalMap: graveNormalTexture,
  aoMap: graveARMTexture,
  roughnessMap: graveARMTexture,
  metalnessMap: graveARMTexture,
});

for (let i = 0; i < 30; i++) {
  const grave = new THREE.Mesh(graveGeometry, graveMaterial);

  //place in circle around the chest
  const angle = Math.random() * Math.PI * 2;
  const radius = 2.5 + Math.random() * 4;
  const x = Math.sin(angle) * radius;
  const z = Math.cos(angle) * radius;
  grave.position.x = x;
  grave.position.y = Math.random() * 0.5;
  grave.position.z = z;

  grave.rotation.x = (Math.random() - 0.5) * 0.4;
  grave.rotation.y = (Math.random() - 0.5) * 0.4;
  grave.rotation.z = (Math.random() - 0.5) * 0.4;

  graves.add(grave);
}

scene.add(graves);

/**
 * Ground
 */
//floor Plane
const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(20, 20, 100, 100),
  new THREE.MeshStandardMaterial({
    map: floorColorTexture,
    alphaMap: floorAlphaTexture,
    normalMap: floorNormalTexture,
    displacementMap: floorDisplacementTexture,
    aoMap: floorARMTexture,
    roughnessMap: floorARMTexture,
    metalnessMap: floorARMTexture,
    transparent: true,
    alphaMap: floorAlphaTexture,
    displacementScale: 0.3,
    displacementBias: -0.2,
  })
);
gui
  .add(floor.material, "displacementScale")
  .min(0)
  .max(1)
  .step(0.001)
  .name("floorDisplacementScale");
gui
  .add(floor.material, "displacementBias")
  .min(-1)
  .max(1)
  .step(0.001)
  .name("floorDisplacementBias");
floor.rotation.x = -Math.PI * 0.5;
scene.add(floor);

/**
 * ShadeHouse geometry
 */
const shadeHouse = new THREE.Group();

// shade top
const shadeTop = new THREE.Mesh(
  new THREE.ConeGeometry(3, 1, 4),
  new THREE.MeshStandardMaterial({
    map: shadeColorTexture,
    normalMap: shadeNormalTexture,
    aoMap: shadeARMTexture,
    metalnessMap: shadeARMTexture,
    roughnessMap: shadeARMTexture,
  })
);
shadeTop.position.y = 3.5
shadeTop.rotation.y = Math.PI * 0.25

//shade house pillar
const pillars = new THREE.Group();
const pillarGeometry = new THREE.CylinderGeometry(0.1, 0.1, 3.4, 10);
const pillarMaterial = new THREE.MeshStandardMaterial({
    map: pillarColorTexture,
    normalMap: pillarNormalTexture,
    aoMap: pillarARMTexture,
    metalnessMap: pillarARMTexture,
    roughnessMap: pillarARMTexture,
})

const pillar1 = new THREE.Mesh(pillarGeometry, pillarMaterial);
const pillar2 = new THREE.Mesh(pillarGeometry, pillarMaterial);
const pillar3 = new THREE.Mesh(pillarGeometry, pillarMaterial);
const pillar4 = new THREE.Mesh(pillarGeometry, pillarMaterial);

pillar1.position.y = 1.7;
pillar2.position.y = 1.7;
pillar3.position.y = 1.7;
pillar4.position.y = 1.7;

pillar1.position.x = 1.7;
pillar2.position.x = -1.7;
pillar3.position.x = 1.7;
pillar4.position.x = -1.7;

pillar1.position.z = 1.7;
pillar2.position.z = -1.7;
pillar3.position.z = -1.7;
pillar4.position.z = 1.7;



pillars.add(pillar1, pillar2, pillar3, pillar4);

shadeHouse.add(shadeTop, pillars);
scene.add(shadeHouse);


/**
 * Lights
 */
// Ambient light
const ambientLight = new THREE.AmbientLight("#86cdff", 0,2);
scene.add(ambientLight);

// Directional light
const directionalLight = new THREE.DirectionalLight("#86cdff",1);
directionalLight.position.set(3, 2, -8);
scene.add(directionalLight);
const directionalLightHelper = new THREE.DirectionalLightHelper(
  directionalLight
);
directionalLight.add(directionalLightHelper);
directionalLightHelper.visible = false;
const directionalLightCameraHelper = new THREE.CameraHelper(
  directionalLight.shadow.camera
);

//tresure point light
const treasureLight = new THREE.PointLight("#e7bc2c", 5);
treasureLight.position.y = 0.6;
treasureLight.position.z = 0.8;
const treasureLightHelper = new THREE.PointLightHelper(treasureLight, 1);
treasureLight.add(treasureLightHelper);
treasureLightHelper.visible = false;
scene.add(treasureLight);

// tresure rectArea light
const treasureRectAreaLight = new THREE.RectAreaLight("#e7bc2c", 5, 1.9, 0.9);
treasureRectAreaLight.position.set(0, 1, 0.5);
scene.add(treasureRectAreaLight);

/**
 * Ghosts
 */
const ghosts = new THREE.Group();
const ghost1 = new THREE.PointLight('#ff0000', 2);
const ghost2 = new THREE.PointLight('#0000ff', 2);
// const ghost3 = new THREE.PointLight('#00ff00', 2);

ghost1.position.y = 0.5;
ghost2.position.y = 0.5;
// ghost3.position.y = 0.5;

ghost1.position.x = 1;
ghost2.position.x = -2;
// ghost3.position.x = 3;

ghosts.add(ghost1,ghost2);
scene.add(ghosts);


/**
 * Shadows
 */
chestTop.castShadow = true;
chestBox.castShadow = true;
directionalLight.castShadow = true;
shadeTop.castShadow = true;
pillar1.castShadow = true;
pillar2.castShadow = true;
pillar3.castShadow = true;
pillar4.castShadow = true;

chestTop.receiveShadow = true;
chestBox.receiveShadow = true;
floor.receiveShadow = true;
pillar1.receiveShadow = true;
pillar2.receiveShadow = true;
pillar3.receiveShadow = true;
pillar4.receiveShadow = true;
shadeTop.receiveShadow = true;



for (const grave of graves.children) {
    grave.castShadow = true;
    grave.receiveShadow = true;
}

ghost1.castShadow = true;
ghost2.castShadow = true;


// ghost3.castShadow = true;

/**
 * Optimize Shadows
 */

//Directional Light
directionalLight.shadow.mapSize.width = 256;
directionalLight.shadow.mapSize.height = 256;
directionalLight.shadow.camera.top = 10;
directionalLight.shadow.camera.right = 10;
directionalLight.shadow.camera.bottom = -10;
directionalLight.shadow.camera.left = -10;
directionalLight.shadow.camera.near = 1;
directionalLight.shadow.camera.far = 20;
directionalLight.add(directionalLightCameraHelper);
directionalLightCameraHelper.visible = false;
const shadowCameraFolder = gui.addFolder("Shadow Camera");
shadowCameraFolder
  .add(directionalLight.shadow.camera, "near", 0.1, 50)
  .name("Near")
  .onChange(() => {
    // Update the shadow camera
    directionalLight.shadow.camera.updateProjectionMatrix();
  });
shadowCameraFolder
  .add(directionalLight.shadow.camera, "far", 0.1, 100)
  .name("Far")
  .onChange(() => {
    // Update the shadow camera
    directionalLight.shadow.camera.updateProjectionMatrix();
  });


//ghost shadows
ghost1.shadow.mapSize.width = 256;
ghost1.shadow.mapSize.height = 256;
ghost1.shadow.camera.near = 0.1;
ghost1.shadow.camera.far = 6;

ghost2.shadow.mapSize.width = 256;
ghost2.shadow.mapSize.height = 256;
ghost2.shadow.camera.near = 0.1;
ghost2.shadow.camera.far = 6;

// ghost3.shadow.mapSize.width = 256;
// ghost3.shadow.mapSize.height = 256;
// ghost3.shadow.camera.near = 0.1;
// ghost3.shadow.camera.far = 6;

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 4;
camera.position.y = 3;
camera.position.z = 5;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
/**
 * Animate
 */
const timer = new Timer();

const tick = () => {
  // Timer
  timer.update();
  const elapsedTime = timer.getElapsed();

  //ghost Animation

  //ghost1 
  const ghost1Angle = elapsedTime * 0.5
  ghost1.position.x = Math.sin(ghost1Angle) *4;
  ghost1.position.y = Math.sin(ghost1Angle) * Math.sin(ghost1Angle * 2.34) * Math.sin(ghost1Angle * 3.45)
  ghost1.position.z = Math.cos(ghost1Angle) *4;

  //ghost2 
  const ghost2Angle = - elapsedTime * 0.2
  ghost2.position.x = Math.sin(ghost2Angle) *6;
  ghost2.position.y = Math.sin(ghost2Angle) * Math.sin(ghost2Angle * 2.34) * Math.sin(ghost2Angle * 3.45)
  ghost2.position.z = Math.cos(ghost2Angle) *6;

//   //ghost3 
//   const ghost3Angle = - elapsedTime * 0.3
//   ghost3.position.x = Math.sin(ghost3Angle) *2;
//   ghost3.position.z = Math.cos(ghost3Angle) *2;


  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
gui.close();
